const AUTH_EXPIRED_MESSAGE = '登录已失效，请重新登录'
const NETWORK_ERROR_MESSAGE = '网络连接失败，请检查网络'
const TIMEOUT_ERROR_MESSAGE = '请求超时，请稍后重试'

/**
 * 判断接口响应是否为 Pig 框架的成功结构。
 * @param {*} payload 接口响应体
 * @returns {boolean} 是否业务成功
 */
export function isApiSuccess(payload) {
  return Boolean(payload) && typeof payload === 'object' && payload.code === 0
}

/**
 * 判断接口响应是否表示官网登录态失效。
 * @param {Response|null} response fetch 响应对象
 * @param {*} payload 已解析的响应体
 * @returns {boolean} 是否登录态失效
 */
export function isAuthExpiredResponse(response, payload) {
  return response?.status === 424 || payload?.code === 424
}

/**
 * 从 Pig R、OAuth2 错误或网关错误中提取可展示的错误信息。
 * @param {*} payload 接口响应体
 * @param {string} fallback 无有效错误信息时的兜底文案
 * @returns {string} 用户可读错误信息
 */
export function extractErrorMessage(payload, fallback) {
  if (typeof payload === 'string') {
    const text = payload.trim()
    return text && !text.startsWith('<') ? text : fallback
  }
  if (!payload || typeof payload !== 'object') return fallback

  const candidates = [
    payload.msg,
    payload.message,
    payload.error_description,
    payload.errorDescription,
    typeof payload.error === 'string' ? payload.error : undefined,
    typeof payload.data === 'string' ? payload.data : undefined
  ]
  const message = candidates.find(
    value => typeof value === 'string' && value.trim() && value.trim().toLowerCase() !== 'error'
  )
  return message?.trim() || fallback
}

/**
 * 安全读取响应 JSON，不因 HTML、空响应或网关文本直接抛出解析异常。
 * @param {Response} response fetch 响应对象
 * @returns {Promise<*>} JSON 对象；空响应返回 null，非法 JSON 返回 undefined
 */
export async function readResponseJson(response) {
  const text = await response.text().catch(() => '')
  if (!text.trim()) return null
  try {
    return JSON.parse(text)
  } catch (error) {
    return undefined
  }
}

/**
 * 生成带 HTTP 状态码的错误兜底文案。
 * @param {Response} response fetch 响应对象
 * @param {string} fallback 业务默认文案
 * @returns {string} 带 HTTP 状态码的兜底文案
 */
export function getHttpStatusFallback(response, fallback) {
  return response ? `${fallback}（HTTP ${response.status}）` : fallback
}

/**
 * 提取 HTTP 错误响应中的后端错误信息。
 * @param {Response} response fetch 响应对象
 * @param {*} payload 已解析的响应体
 * @param {string} fallback 业务默认文案
 * @returns {string} 用户可读错误信息
 */
export function getHttpErrorMessage(response, payload, fallback) {
  return extractErrorMessage(payload, getHttpStatusFallback(response, fallback))
}

/**
 * 将 fetch 网络异常转换为稳定的中文提示。
 * @param {Error} error fetch 抛出的异常
 * @param {string} fallback 默认网络文案
 * @returns {string} 用户可读错误信息
 */
export function getNetworkErrorMessage(error, fallback = NETWORK_ERROR_MESSAGE) {
  if (error?.name === 'AbortError') return '请求已取消，请重试'
  if (error?.code === 'ECONNABORTED') return TIMEOUT_ERROR_MESSAGE
  return fallback
}

/**
 * 清理官网登录态并广播登录失效事件。
 */
export function clearWebsiteAuthSession() {
  localStorage.removeItem('dzzj_website_token')
  localStorage.removeItem('dzzj_website_refresh_token')
  window.dispatchEvent(new CustomEvent('website-auth-expired'))
}

export const AUTH_EXPIRED_ERROR_MESSAGE = AUTH_EXPIRED_MESSAGE
export const NETWORK_ERROR_MESSAGE_TEXT = NETWORK_ERROR_MESSAGE
export const TIMEOUT_ERROR_MESSAGE_TEXT = TIMEOUT_ERROR_MESSAGE
